import {
  Box,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button as MuiButton,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import BackBtn from "../../widgets/BackBtn";
import useApiEp from "../../hooks/useApiEp";
import { getHeader } from "../../utils/util";
import { API_ENDPOINT } from "../../utils/constants";

/* ─── Status helpers ─────────────────────────────────────── */
const CAMPAIGN_STATUS = {
  0: {
    label: "Ready",
    color: "#6366f1",
    bg: "rgba(99,102,241,0.12)",
    dot: "#6366f1",
  },
  1: {
    label: "Running",
    color: "#10d9a0",
    bg: "rgba(16,217,160,0.12)",
    dot: "#10d9a0",
  },
  2: {
    label: "Complete",
    color: "#10b981",
    bg: "rgba(16,185,129,0.12)",
    dot: "#10b981",
  },
  3: {
    label: "Low Balance",
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.12)",
    dot: "#f59e0b",
  },
  4: {
    label: "Parameter Error",
    color: "#ef4444",
    bg: "rgba(239,68,68,0.12)",
    dot: "#ef4444",
  },
  5: {
    label: "Error",
    color: "#dc2626",
    bg: "rgba(220,38,38,0.12)",
    dot: "#dc2626",
  },
  6: {
    label: "Cancelling",
    color: "#f97316",
    bg: "rgba(249,115,22,0.12)",
    dot: "#f97316",
  },
  7: {
    label: "Cancelled",
    color: "#64748b",
    bg: "rgba(100,116,139,0.12)",
    dot: "#64748b",
  },
  8: {
    label: "Pausing",
    color: "#a855f7",
    bg: "rgba(168,85,247,0.12)",
    dot: "#a855f7",
  },
  9: {
    label: "Paused",
    color: "#7c3aed",
    bg: "rgba(124,58,237,0.12)",
    dot: "#7c3aed",
  },
  10: {
    label: "Resuming",
    color: "#3b82f6",
    bg: "rgba(59,130,246,0.12)",
    dot: "#3b82f6",
  },
  21: {
    label: "DND Processing",
    color: "#0ea5e9",
    bg: "rgba(14,165,233,0.12)",
    dot: "#0ea5e9",
  },
};

const fmt = (v) => (v === undefined || v === null || v === "" ? "—" : v);
const pct = (a, b) => (b > 0 ? ((a / b) * 100).toFixed(1) + "%" : "0%");

/* ─── Tiny sub-components ────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const s = CAMPAIGN_STATUS[status] ?? CAMPAIGN_STATUS[0];
  const isRunning =
    status === 1 ||
    status === 6 ||
    status === 8 ||
    status === 10 ||
    status === 21;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 11px",
        borderRadius: 999,
        background: s.bg,
        border: `1px solid ${s.color}30`,
        fontSize: 11,
        fontWeight: 700,
        color: s.color,
        textTransform: "uppercase",
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: s.dot,
          boxShadow: `0 0 6px ${s.dot}99`,
          animation: isRunning ? "pulse 1.4s ease-in-out infinite" : "none",
        }}
      />
      {s.label}
    </span>
  );
};

const MetricPill = ({ label, value, accent, small }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 3,
      padding: small ? "8px 14px" : "12px 18px",
      background: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: 10,
      minWidth: small ? 70 : 90,
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    }}
  >
    <span
      style={{
        fontSize: small ? 18 : 24,
        fontWeight: 700,
        color: accent ?? "#111827",
      }}
    >
      {fmt(value)}
    </span>
    <span
      style={{
        fontSize: 9,
        color: "#64748b",
        textTransform: "uppercase",
        fontWeight: 600,
        textAlign: "center",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  </div>
);

const ProgressBar = ({ value, max, color }) => {
  const ratio = max > 0 ? Math.min(value / max, 1) : 0;
  return (
    <div
      style={{
        height: 4,
        background: "#e2e8f0",
        borderRadius: 2,
        overflow: "hidden",
        width: "100%",
      }}
    >
      <div
        style={{
          height: "100%",
          width: `${ratio * 100}%`,
          background: `linear-gradient(90deg, ${color}dd, ${color})`,
          borderRadius: 2,
          transition: "width 0.8s cubic-bezier(.4,0,.2,1)",
          boxShadow: `0 0 8px ${color}66`,
        }}
      />
    </div>
  );
};

/* ─── Campaign Card ──────────────────────────────────────── */
const CampaignCard = ({ campaign, index, onRequestDownload }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="campaign-card"
      style={{
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        overflow: "hidden",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#10d9a0";
        e.currentTarget.style.boxShadow = "0 12px 40px rgba(16,217,160,0.12)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#e2e8f0";
        e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.06)";
      }}
    >
      {/* Header row */}
      <div style={{ padding: "18px 22px", borderBottom: "1px solid #e2e8f0" }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#111827",
                }}
              >
                {campaign.campaignName}
              </span>
              <StatusBadge status={campaign.campaignStatus} />
              {campaign.isRetry === 1 && (
                <span
                  style={{
                    fontSize: 10,
                    color: "#f59e0b",
                    background: "rgba(245,158,11,0.08)",
                    border: "1px solid rgba(245,158,11,0.2)",
                    borderRadius: 4,
                    padding: "2px 7px",
                    fontWeight: 700,
                  }}
                >
                  RETRY
                </span>
              )}
            </div>
            <div
              style={{
                marginTop: 4,
                display: "flex",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <span style={{ fontSize: 11, color: "#64748b" }}>
                ID:{" "}
                <span style={{ color: "#334155" }}>#{campaign.campaignId}</span>
              </span>
              <span style={{ fontSize: 11, color: "#64748b" }}>
                Scheduled:{" "}
                <span style={{ color: "#334155" }}>
                  {fmt(campaign.scheduleTime)}
                </span>
              </span>
              {campaign.endTime && (
                <span style={{ fontSize: 11, color: "#64748b" }}>
                  Ended:{" "}
                  <span style={{ color: "#334155" }}>
                    {fmt(campaign.endTime)}
                  </span>
                </span>
              )}
            </div>
          </div>

          {/* Download Report Button - Top right */}
          <div style={{ flexShrink: 0 }}>
            <button
              onClick={() => onRequestDownload(campaign.campaignId)}
              style={{
                padding: "9px 18px",
                background: "#10d9a0",
                color: "#ffffff",
                border: "none",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                boxShadow: "0 4px 14px rgba(16,217,160,0.25)",
                transition: "all 0.2s ease",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#0fb98f";
                e.currentTarget.style.boxShadow =
                  "0 6px 18px rgba(16,217,160,0.35)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#10d9a0";
                e.currentTarget.style.boxShadow =
                  "0 4px 14px rgba(16,217,160,0.25)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              📥 Download Report
            </button>
          </div>
        </div>
      </div>

      {/* Primary metrics */}
      <div style={{ padding: "18px 22px" }}>
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <MetricPill label="Uploaded" value={campaign.numbersUploaded} />
          <MetricPill
            label="Processed"
            value={campaign.numbersProcessed}
            accent="#6366f1"
          />
          <MetricPill
            label="Connected"
            value={campaign.callsConnected}
            accent="#10d9a0"
          />
          <MetricPill label="DND" value={campaign.dndCount} accent="#ef4444" />
          <MetricPill
            label="Failed"
            value={campaign.failedCount}
            accent="#f97316"
          />
          <MetricPill label="SMS" value={campaign.smsCount} accent="#3b82f6" />
        </div>

        {/* Progress bars */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  color: "#64748b",
                  textTransform: "uppercase",
                }}
              >
                Processing
              </span>
              <span style={{ fontSize: 10, color: "#6366f1" }}>
                {pct(campaign.numbersProcessed, campaign.numbersUploaded)}
              </span>
            </div>
            <ProgressBar
              value={campaign.numbersProcessed}
              max={campaign.numbersUploaded}
              color="#6366f1"
            />
          </div>
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  color: "#64748b",
                  textTransform: "uppercase",
                }}
              >
                Connect Rate
              </span>
              <span style={{ fontSize: 10, color: "#10d9a0" }}>
                {pct(campaign.callsConnected, campaign.numbersProcessed)}
              </span>
            </div>
            <ProgressBar
              value={campaign.callsConnected}
              max={campaign.numbersProcessed || 1}
              color="#10d9a0"
            />
          </div>
        </div>

        {/* Expand toggle */}
        <button
          onClick={() => setExpanded((e) => !e)}
          style={{
            marginTop: 14,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#64748b",
            fontSize: 11,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 5,
            padding: 0,
            transition: "color 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#10d9a0")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#64748b")}
        >
          <span
            style={{
              display: "inline-block",
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          >
            ▶
          </span>
          {expanded ? "HIDE DETAILS" : "SHOW DETAILS"}
        </button>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div
          style={{
            padding: "0 22px 20px",
            borderTop: "1px solid #e2e8f0",
            paddingTop: 16,
          }}
        >
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <MetricPill
              label="Pulses"
              value={campaign.pulses}
              accent="#8b5cf6"
              small
            />
            <MetricPill
              label="Retries"
              value={campaign.retries}
              accent="#f59e0b"
              small
            />
            <MetricPill
              label="DTMF Total"
              value={campaign.dtmfCount}
              accent="#10b981"
              small
            />
            <MetricPill
              label="DTMF 1"
              value={campaign.dtmf1Count}
              accent="#10b981"
              small
            />
            <MetricPill
              label="DTMF 2"
              value={campaign.dtmf2Count}
              accent="#10b981"
              small
            />
            <MetricPill
              label="TTS"
              value={campaign.ttsCount}
              accent="#3b82f6"
              small
            />
            <MetricPill label="Webhook Log" value={campaign.webhookLog} small />
          </div>

          <div
            style={{
              marginTop: 14,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: 8,
            }}
          >
            {[
              ["Template ID", campaign.templateId],
              ["TTS Flow ID", campaign.ttsFlowId || "—"],
              ["Full Report", campaign.fullReportStatus],
              ["Webhook", campaign.webhook ? "Yes" : "No"],
              ["SMS Webhook", campaign.smsWebhookFlag ? "Yes" : "No"],
              ["Retry Count", campaign.retryCount],
            ].map(([k, v]) => (
              <div
                key={k}
                style={{
                  background: "#f8fafc",
                  borderRadius: 8,
                  padding: "8px 12px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    fontSize: 9,
                    color: "#64748b",
                    textTransform: "uppercase",
                    marginBottom: 2,
                  }}
                >
                  {k}
                </div>
                <div style={{ fontSize: 13, color: "#334155" }}>{fmt(v)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Summary Bar ────────────────────────────────────────── */
const SummaryBar = ({ data }) => {
  const totals = data.reduce(
    (acc, c) => ({
      uploaded: acc.uploaded + (c.numbersUploaded || 0),
      processed: acc.processed + (c.numbersProcessed || 0),
      connected: acc.connected + (c.callsConnected || 0),
      failed: acc.failed + (parseInt(c.failedCount) || 0),
      dnd: acc.dnd + (c.dndCount || 0),
    }),
    { uploaded: 0, processed: 0, connected: 0, failed: 0, dnd: 0 }
  );

  const stats = [
    { label: "Campaigns", value: data.length, accent: "#111827" },
    { label: "Total Uploaded", value: totals.uploaded, accent: "#475569" },
    { label: "Processed", value: totals.processed, accent: "#6366f1" },
    { label: "Connected", value: totals.connected, accent: "#10d9a0" },
    { label: "Failed", value: totals.failed, accent: "#ef4444" },
    { label: "DND", value: totals.dnd, accent: "#f97316" },
  ];

  return (
    <div
      style={{
        background:
          "linear-gradient(135deg, rgba(16,217,160,0.08), rgba(99,102,241,0.08))",
        border: "1px solid rgba(16,217,160,0.25)",
        borderRadius: 14,
        padding: "16px 22px",
        marginBottom: 24,
        display: "flex",
        gap: 0,
        flexWrap: "wrap",
        boxShadow: "0 4px 20px rgba(16,217,160,0.06)",
      }}
    >
      {stats.map((s, i) => (
        <React.Fragment key={s.label}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "4px 20px",
              flex: 1,
              minWidth: 80,
            }}
          >
            <span
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: s.accent,
              }}
            >
              {s.value}
            </span>
            <span
              style={{
                fontSize: 9,
                color: "#64748b",
                textTransform: "uppercase",
                marginTop: 2,
              }}
            >
              {s.label}
            </span>
          </div>
          {i < stats.length - 1 && (
            <div
              style={{
                width: 1,
                background: "rgba(15,23,42,0.1)",
                margin: "4px 0",
                alignSelf: "stretch",
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

/* ─── Filters / Sort Bar ─────────────────────────────────── */
const FilterBar = ({ filter, setFilter, sort, setSort }) => {
  const statuses = [
    { value: "all", label: "All" },
    { value: "0", label: "Scheduled" },
    { value: "1", label: "Running" },
    { value: "2", label: "Completed" },
  ];

  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        marginBottom: 20,
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 4,
          background: "#f1f5f9",
          padding: 4,
          borderRadius: 10,
          border: "1px solid #e2e8f0",
        }}
      >
        {statuses.map((s) => (
          <button
            key={s.value}
            onClick={() => setFilter(s.value)}
            style={{
              padding: "5px 14px",
              borderRadius: 7,
              border: "none",
              cursor: "pointer",
              fontSize: 11,
              fontWeight: 600,
              background:
                filter === s.value ? "rgba(16,217,160,0.15)" : "transparent",
              color: filter === s.value ? "#10d9a0" : "#64748b",
              transition: "all 0.15s",
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      <select
        value={sort}
        onChange={(e) => setSort(e.target.value)}
        style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
          color: "#334155",
          padding: "6px 12px",
          fontSize: 11,
          cursor: "pointer",
          outline: "none",
        }}
      >
        <option value="schedule">Sort: Schedule Time</option>
        <option value="name">Sort: Name</option>
        <option value="connected">Sort: Connected</option>
        <option value="uploaded">Sort: Uploaded</option>
      </select>
    </div>
  );
};

/* ─── Skeleton Loader ────────────────────────────────────── */
const SkeletonCard = ({ i }) => (
  <div
    style={{
      background: "#f8fafc",
      borderRadius: 16,
      border: "1px solid #e2e8f0",
      padding: 22,
      animation: `pulse 1.6s ease-in-out ${i * 0.15}s infinite`,
      minHeight: 160,
    }}
  >
    <div
      style={{
        height: 16,
        width: "40%",
        background: "rgba(15,23,42,0.07)",
        borderRadius: 4,
        marginBottom: 10,
      }}
    />
    <div
      style={{
        height: 11,
        width: "60%",
        background: "rgba(15,23,42,0.05)",
        borderRadius: 4,
        marginBottom: 18,
      }}
    />
    <div style={{ display: "flex", gap: 8 }}>
      {[...Array(5)].map((_, j) => (
        <div
          key={j}
          style={{
            height: 60,
            flex: 1,
            background: "rgba(15,23,42,0.05)",
            borderRadius: 8,
          }}
        />
      ))}
    </div>
  </div>
);

/* ─── Main Page ──────────────────────────────────────────── */
const LiveVoiceReport = () => {
  const { data, isLoading, isError, error } = useApiEp(
    `/voice/campaign/live-report`
  );
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("schedule");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  // Handle Download Report POST
  const handleRequestDownload = async (campaignId) => {
    try {
      const res = await fetch(`${API_ENDPOINT}/voice/download-request`, {
        method: "POST",
        ...getHeader(),
        body: JSON.stringify({ campaignId }),
      });

      const result = await res.json();

      if (res.ok) {
        setModalMessage(
          result.message || "Report request submitted successfully"
        );
      } else {
        setModalMessage(result.message || "Failed to request report");
      }
      setModalOpen(true);
    } catch (err) {
      setModalMessage("Network error. Please try again.");
      setModalOpen(true);
    }
  };

  useEffect(() => {
    const t = setInterval(() => {}, 30000); // keep auto-refresh feel
    return () => clearInterval(t);
  }, []);

  const campaigns = Array.isArray(data) ? data : [];

  const filtered = campaigns
    .filter((c) => filter === "all" || String(c.campaignStatus) === filter)
    .sort((a, b) => {
      if (sort === "name") return a.campaignName.localeCompare(b.campaignName);
      if (sort === "connected")
        return (b.callsConnected || 0) - (a.callsConnected || 0);
      if (sort === "uploaded")
        return (b.numbersUploaded || 0) - (a.numbersUploaded || 0);
      return new Date(a.scheduleTime) - new Date(b.scheduleTime);
    });

  return (
    <>
      {/* Global styles */}
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; } to { opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        body { background: #f8fafc !important; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: rgba(16,217,160,0.4); border-radius: 3px; }
      `}</style>

      <div
        style={{
          minHeight: "100vh",
          background:
            "radial-gradient(ellipse 80% 50% at 50% -10%, rgba(16,217,160,0.06) 0%, transparent 60%), #f8fafc",
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ pt: 3, pb: 6, position: "relative", zIndex: 1 }}
        >
          <BackBtn />

          {/* Page header */}
          <div style={{ marginBottom: 28, marginTop: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                marginBottom: 4,
              }}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: "#10d9a0",
                  boxShadow: "0 0 12px #10d9a0",
                  animation: "pulse 1.4s ease-in-out infinite",
                }}
              />
              <h1
                style={{
                  margin: 0,
                  fontSize: 28,
                  fontWeight: 700,
                  color: "#111827",
                }}
              >
                Live Voice Report
              </h1>
              <span
                style={{
                  fontSize: 10,
                  color: "#10d9a0",
                  background: "rgba(16,217,160,0.1)",
                  border: "1px solid rgba(16,217,160,0.3)",
                  borderRadius: 4,
                  padding: "3px 8px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                Live
              </span>
            </div>
            <p style={{ margin: 0, fontSize: 11, color: "#64748b" }}>
              CAMPAIGN MONITORING DASHBOARD · AUTO-REFRESH ACTIVE
            </p>
          </div>

          {/* Error state */}
          {isError && (
            <div
              style={{
                padding: "20px 24px",
                borderRadius: 12,
                background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.25)",
                color: "#ef4444",
                fontSize: 13,
              }}
            >
              ⚠ Failed to load report data.{" "}
              {error?.message ?? "Please try again."}
            </div>
          )}

          {/* Loading skeleton */}
          {isLoading && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {[...Array(3)].map((_, i) => (
                <SkeletonCard key={i} i={i} />
              ))}
            </div>
          )}

          {/* Data */}
          {!isLoading && !isError && campaigns.length > 0 && (
            <>
              <SummaryBar data={campaigns} />
              <FilterBar
                filter={filter}
                setFilter={setFilter}
                sort={sort}
                setSort={setSort}
              />
              {filtered.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "60px 20px",
                    color: "#64748b",
                    fontSize: 12,
                  }}
                >
                  NO CAMPAIGNS MATCH THIS FILTER
                </div>
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 14 }}
                >
                  {filtered.map((c, i) => (
                    <CampaignCard
                      key={c.campaignId}
                      campaign={c}
                      index={i}
                      onRequestDownload={handleRequestDownload}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Empty state */}
          {!isLoading && !isError && campaigns.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "80px 20px",
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 16,
                  margin: "0 auto 16px",
                  background: "#f1f5f9",
                  border: "1px solid #e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 28,
                  color: "#64748b",
                }}
              >
                📡
              </div>
              <p
                style={{
                  color: "#64748b",
                  fontSize: 11,
                  textTransform: "uppercase",
                }}
              >
                No campaigns found
              </p>
            </div>
          )}

          {/* Success / Info Modal */}
          <Dialog
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              style: {
                borderRadius: 16,
                boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
              },
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                fontSize: 20,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              <span style={{ fontSize: 28, color: "#10d9a0" }}>📊</span>
              Report Request
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <DialogContentText
                sx={{
                  fontSize: 15.5,
                  lineHeight: 1.65,
                  color: "#334155",
                }}
              >
                {modalMessage}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ pb: 3, px: 3 }}>
              <MuiButton
                onClick={() => setModalOpen(false)}
                variant="contained"
                sx={{
                  background: "#10d9a0",
                  fontWeight: 700,
                  px: 5,
                  py: 1.2,
                  borderRadius: 8,
                  "&:hover": { background: "#0fb98f" },
                }}
              >
                OK
              </MuiButton>
            </DialogActions>
          </Dialog>
        </Container>
      </div>
    </>
  );
};

export default LiveVoiceReport;
